import { getFilenameFromPath } from "@/lib/utils";
import {
  isGroupedAttachments,
  hasAnyFiles,
  type Attachments,
  type FileMetadata,
} from "@/modules/agreement/utils";
import {
  AttachmentsDisplaySection,
  type AttachmentGroup,
  type AttachmentFileItem,
} from "@/components/AttachmentsDisplaySection";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { AGREEMENT_DOCUMENTS_ATTACHMENT_TYPE, Module } from "@/constants";

interface AgreementAttachmentsDisplayProps {
  attachments: Attachments;
  title?: string;
  attachmentType?: string;
}

function toAttachmentFileItem(
  file: FileMetadata,
  fileIndex: number,
): AttachmentFileItem {
  return {
    filename: getFilenameFromPath(file.path, `file_${fileIndex + 1}`),
    path: file.path,
    size: file.size,
    confirmed_at: file.confirmed_at,
  };
}

function toGroups(attachments: Attachments, title?: string): AttachmentGroup[] {
  if (isGroupedAttachments(attachments)) {
    return attachments.map((group) => ({
      key: group.key,
      title: group.title,
      files: group.files.map((file, i) => toAttachmentFileItem(file, i)),
    }));
  }
  const simple = attachments as FileMetadata[];
  return [
    {
      key: "agreement-files",
      title: title ?? "",
      files: simple.map((file, i) => toAttachmentFileItem(file, i)),
    },
  ];
}

export const AgreementAttachmentsDisplay = ({
  attachments,
  title,
  attachmentType,
}: AgreementAttachmentsDisplayProps) => {
  const groups = hasAnyFiles(attachments) ? toGroups(attachments, title) : [];
  const { agreements_documents_read } = useModulePermissions(Module.Agreements);
  const canReadAttachments =
    attachmentType === AGREEMENT_DOCUMENTS_ATTACHMENT_TYPE
      ? agreements_documents_read
      : true;

  return (
    <AttachmentsDisplaySection
      groups={groups}
      attachmentType={attachmentType}
      canRead={canReadAttachments}
    />
  );
};
